import { ChevronLeft, ChevronRight } from "lucide-preact";
import type { ComponentChildren } from "preact";
import { type MutableRef, useEffect, useRef, useState } from "preact/hooks";

export type CarouselProps = {
  children: ComponentChildren[];
};

const GAP = 16;
const ARROW_CLASS =
  "absolute top-1/2 -translate-y-1/2 z-50 w-8 h-8 rounded-full bg-white/90 dark:bg-neutral-800/90 shadow-md flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white dark:hover:bg-neutral-700";

type Slides = { container: MutableRef<HTMLDivElement | null>; items: MutableRef<(HTMLDivElement | null)[]> };

/** The index of the slide that is more than half in view, kept in step by an IntersectionObserver. */
const useCurrentSlide = ({ container, items }: Slides, count: number): number => {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (!container.current) {
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const found = items.current.findIndex((item) => item === entry.target);
          if (entry.isIntersecting && entry.intersectionRatio > 0.5 && found !== -1) {
            setIndex(found);
          }
        }
      },
      { root: container.current, threshold: 0.5 },
    );
    for (const item of items.current) {
      if (item) {
        observer.observe(item);
      }
    }
    return () => observer.disconnect();
  }, [count]);

  return index;
};

const Dots = ({ count, current }: { count: number; current: number }) => (
  <div class="flex items-center justify-center gap-1.5">
    {Array.from({ length: count }, (_, index) => (
      <span
        key={index}
        class={`transition-all duration-200 rounded-full ${
          index === current ? "w-2 h-2 bg-gray-800 dark:bg-gray-300" : "w-1.5 h-1.5 bg-gray-400 dark:bg-gray-600 hover:bg-gray-600 dark:hover:bg-gray-400"
        }`}
      />
    ))}
  </div>
);

export const Carousel = ({ children }: CarouselProps) => {
  const slides: Slides = { container: useRef<HTMLDivElement | null>(null), items: useRef<(HTMLDivElement | null)[]>([]) };
  const current = useCurrentSlide(slides, children.length);

  const scrollTo = (index: number) => {
    const container = slides.container.current;
    container?.scrollTo({ left: index * (container.clientWidth + GAP), behavior: "smooth" });
  };

  if (children.length === 0) {
    return null;
  }

  return (
    <div class="relative group">
      {current > 0 && (
        <button type="button" onClick={() => scrollTo(current - 1)} class={`${ARROW_CLASS} left-2`} aria-label="Previous slide">
          <ChevronLeft size={18} class="text-gray-700 dark:text-gray-300" />
        </button>
      )}
      {current < children.length - 1 && (
        <button type="button" onClick={() => scrollTo(current + 1)} class={`${ARROW_CLASS} right-2`} aria-label="Next slide">
          <ChevronRight size={18} class="text-gray-700 dark:text-gray-300" />
        </button>
      )}
      <div
        ref={slides.container}
        class="grid grid-flow-col auto-cols-[100%] overflow-x-auto scrollbar-hide snap-x snap-mandatory"
        style={{ gap: `${GAP}px`, scrollbarWidth: "none", scrollBehavior: "smooth", overscrollBehaviorX: "none" }}
      >
        {children.map((child, index) => (
          <div
            key={index}
            ref={(element) => {
              slides.items.current[index] = element;
            }}
            class="snap-start h-full"
            onDragStart={(event) => event.preventDefault()}
          >
            {child}
          </div>
        ))}
      </div>
      <Dots count={children.length} current={current} />
    </div>
  );
};
